/**
 * `kpi_config` — Speicherform, Laden/Speichern und Uebersetzung in den Vertrag
 * der Engine.
 *
 * Die SPEICHERFORM bleibt unveraendert (`offsets` in Euro je Monatsindex,
 * `workload_settings` mit `price_ranges` und `assignment_minutes`), damit
 * bestehende Konfigurationen weiter laden. Uebersetzt wird erst beim Lesen.
 */
import { useCallback, useEffect, useState } from 'react';
import type { UserProfile } from '../../types';
import type { MonthSeries, WorkloadSettings } from '../../services/cockpitTypes';
import { centsFromEuro } from '../../services/money';
import { supabase } from '../../services/supabaseClient';
import { CAP_HOURS, type CockpitProject } from './cockpitUi';

export interface StoredPriceRange { min: number; max: number; minutes: number; }

export interface StoredWorkload {
    price_ranges: StoredPriceRange[];
    assignment_minutes: Record<string, number>;
}

/** Externer Umsatz je Monat in EURO, Schluessel 0–11. */
export type StoredOffsets = Record<string, number>;

export const DEFAULT_WORKLOAD: StoredWorkload = {
    price_ranges: [
        { min: 0, max: 250, minutes: 60 },
        { min: 251, max: 350, minutes: 120 },
        { min: 351, max: 500, minutes: 240 },
        { min: 501, max: 1000, minutes: 360 },
        { min: 1001, max: 99999, minutes: 480 },
    ],
    // Fixzeit JE STUECK, nicht je Projekt: `einweisung` ist die einzige
    // Kontingentposition dieser Liste. 60 Min. = Einweisung auf der Baustelle
    // samt Blatt, Unterschriften und Ablage.
    assignment_minutes: {
        vorank: 90, sigeplan: 240, asplan: 240, einweisung: 60,
        unterlage: 180, site_rules: 60, planning_general: 60,
    },
};

/** Fallback, wenn keine Begehungspreise vorliegen — die mittlere Staffel. */
const FALLBACK_MINUTES = 120;

export const normalizeWorkload = (raw: unknown): StoredWorkload => {
    const value = (raw ?? {}) as Partial<StoredWorkload>;
    return {
        price_ranges: Array.isArray(value.price_ranges) && value.price_ranges.length > 0
            ? value.price_ranges
            : DEFAULT_WORKLOAD.price_ranges,
        assignment_minutes: { ...DEFAULT_WORKLOAD.assignment_minutes, ...(value.assignment_minutes ?? {}) },
    };
};

export const normalizeOffsets = (raw: unknown): StoredOffsets => {
    const out: StoredOffsets = {};
    const value = (raw ?? {}) as Record<string, unknown>;
    for (let i = 0; i < 12; i++) {
        const euro = Number(value[String(i)]);
        if (Number.isFinite(euro) && euro !== 0) out[String(i)] = euro;
    }
    return out;
};

/**
 * Euro je Monat → Cent je Monat, zwoelf Werte.
 *
 * Ueber `centsFromEuro` und nicht ueber `Math.round(euro * 100)`: Math.round
 * rundet -0,005 auf -0 statt auf -1 und waere damit bei negativen Korrektur-
 * betraegen unsymmetrisch; ausserdem faengt roundCents die IEEE-754-Falle ab
 * (1,005 * 100 ergibt 100.49999999999999). Eine Geldumrechnung im Haus.
 */
export const offsetsToCents = (offsets: StoredOffsets): MonthSeries =>
    Array.from({ length: 12 }, (_, i) => centsFromEuro(Number(offsets[String(i)]) || 0));

/**
 * Minuten je Stueck — der LETZTE Ersatzwert (`default`), wenn weder
 * Zuordnung, Titel noch Preisstaffel der Position eine Zahl liefern
 * (cockpitWorkload.minutesForPosition).
 *
 * Der Vertrag kennt nur eine Zahl je Zuordnung, die Speicherform dagegen eine
 * Preisstaffel. Uebersetzt wird ueber den MITTLEREN Begehungspreis im Bestand:
 * die Staffel, in die er faellt, gilt fuer alle Begehungen. Das ist die
 * einzige Stelle, an der die Uebersetzung verlustbehaftet ist.
 */
const inspectionMinutes = (stored: StoredWorkload, projects: CockpitProject[]): number => {
    const prices: number[] = [];
    projects.forEach((p) => (p.project_services ?? []).forEach((s) => {
        if ((s.assignment ?? '') !== 'inspection') return;
        const price = Number(s.unit_price) || 0;
        if (price > 0) prices.push(price);
    }));
    if (prices.length === 0) return FALLBACK_MINUTES;
    prices.sort((a, b) => a - b);
    const median = prices[Math.floor(prices.length / 2)];
    const range = stored.price_ranges.find((r) => median >= r.min && median <= r.max);
    return range?.minutes ?? FALLBACK_MINUTES;
};

/**
 * Ein gespeicherter Minutenwert, oder null wenn der Schluessel fehlt bzw.
 * unbrauchbar ist. `null` aus der Datenbank wuerde ueber `Number` zu 0 und
 * damit zu „null Minuten je Begehung" — deshalb vor der Umwandlung geprueft.
 */
const storedMinutes = (raw: unknown): number | null => {
    if (raw === null || raw === undefined || raw === '') return null;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : null;
};

export const toWorkloadSettings = (stored: StoredWorkload, projects: CockpitProject[]): WorkloadSettings => {
    // GESPEICHERTE WERTE GEWINNEN (Befund G11), aber nur die wirklich
    // gepflegten: `inspection` steht NUR dann in der Tabelle, wenn es
    // gespeichert ist. Frueher stand dort immer der Median-Ersatz — und weil
    // `minutesForPosition` die Zuordnungstabelle zuerst liest, haette er Titel-
    // (Tagespauschale = 8 h) und Preisregel je Position nie zum Zug kommen
    // lassen. Der Median ist nur noch der LETZTE Rueckfall (`default`).
    const minutesPerInspection: Record<string, number> & { default: number } = {
        default: storedMinutes(stored.assignment_minutes.default) ?? inspectionMinutes(stored, projects),
    };
    for (const [key, raw] of Object.entries(stored.assignment_minutes)) {
        if (key === 'default') continue;
        const value = storedMinutes(raw);
        if (value !== null) minutesPerInspection[key] = value;
    }
    // Preisstaffel je Position (alte `gM`-Regel): Einzelpreis DIESER Zeile.
    const priceRanges = (stored.price_ranges ?? [])
        .map((r) => ({ min: Number(r?.min), max: Number(r?.max), minutes: Number(r?.minutes) }))
        .filter((r) => Number.isFinite(r.min) && Number.isFinite(r.max) && Number.isFinite(r.minutes) && r.minutes > 0);
    return { minutesPerInspection, capacityHoursPerMonth: CAP_HOURS, priceRanges };
};

// ── Laden und Speichern ──────────────────────────────────────────────────

/** Die Spalten von `kpi_config`, die gelesen werden. */
interface KpiConfigRow {
    offsets?: unknown;
    workload_settings?: unknown;
    updated_by_name?: string | null;
    updated_at?: string | null;
}

/**
 * Die geladene Konfiguration TRAEGT IHR JAHR MIT SICH. Ohne diesen Schluessel
 * blieben beim Jahreswechsel die Werte des Vorjahres stehen, bis die neue Zeile
 * eintrifft — und ein Speichern in diesem Fenster schriebe die ALTEN Betraege
 * unter das NEUE Jahr (Befund G2). `failed` ist etwas anderes als „noch keine
 * Zeile": ein Ladefehler sperrt das Speichern, eine fehlende Zeile nicht (G3).
 */
interface LoadedConfig {
    year: number;
    offsets: StoredOffsets;
    workload: StoredWorkload;
    lastEdit: { name: string; date: string } | null;
    failed: boolean;
}

/** Eine Kennung statt `{}` je Aufbau: sonst rechnet `offsetsToCents` jedes Mal neu. */
const NO_OFFSETS: StoredOffsets = {};

export interface KpiConfigHandle {
    offsets: StoredOffsets;
    workload: StoredWorkload;
    lastEdit: { name: string; date: string } | null;
    /** Laden fehlgeschlagen — die Oberflaeche zeigt dafuer eine Warnzeile. */
    failed: boolean;
    /** null heisst „speichern erlaubt", sonst steht hier der Grund der Sperre. */
    saveBlocked: string | null;
    saving: boolean;
    setOffsets: (next: StoredOffsets) => void;
    setWorkload: (next: StoredWorkload) => void;
    /** true bei Erfolg. */
    save: () => Promise<boolean>;
}

export const useKpiConfig = (
    companyName: string | null,
    year: number,
    currentUser: UserProfile | null,
): KpiConfigHandle => {
    const [config, setConfig] = useState<LoadedConfig | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!companyName) return undefined;
        let cancelled = false;
        (async () => {
            const { data, error } = await supabase
                .from('kpi_config').select('*')
                .eq('company_name', companyName).eq('year', year)
                .maybeSingle();
            if (cancelled) return;
            // EIN FEHLER IST KEINE LEERE KONFIGURATION (Befund G3): mit Vorgabe-
            // werten weiterzuarbeiten hiesse, dass das naechste Speichern die
            // echten Daten ueberschreibt.
            if (error) {
                console.error(error);
                setConfig({ year, offsets: NO_OFFSETS, workload: DEFAULT_WORKLOAD, lastEdit: null, failed: true });
                return;
            }
            const row = (data ?? null) as KpiConfigRow | null;
            setConfig({
                year,
                offsets: normalizeOffsets(row?.offsets),
                workload: normalizeWorkload(row?.workload_settings),
                lastEdit: row?.updated_by_name && row.updated_at ? { name: row.updated_by_name, date: row.updated_at } : null,
                failed: false,
            });
        })();
        return () => { cancelled = true; };
    }, [companyName, year]);

    // Nur eine Konfiguration MIT PASSENDEM JAHR gilt. Alles andere ist ein noch
    // laufender Ladevorgang: Nullwerte rechnen, Speichern gesperrt.
    const loaded = config && config.year === year ? config : null;

    const patch = useCallback(
        (next: Partial<LoadedConfig>) => setConfig((p) => (p && p.year === year && !p.failed ? { ...p, ...next } : p)),
        [year],
    );
    const setOffsets = useCallback((offsets: StoredOffsets) => patch({ offsets }), [patch]);
    const setWorkload = useCallback((workload: StoredWorkload) => patch({ workload }), [patch]);

    const save = useCallback(async (): Promise<boolean> => {
        if (!companyName || !loaded || loaded.failed) return false;
        const name = currentUser
            ? `${currentUser.first_name ?? ''} ${currentUser.last_name ?? ''}`.trim() || 'Admin'
            : 'Admin';
        setSaving(true);
        try {
            // Jahr und Werte aus DEMSELBEN Satz, nie aus zwei Quellen.
            const { error } = await supabase.from('kpi_config').upsert({
                company_name: companyName,
                year: loaded.year,
                offsets: loaded.offsets,
                workload_settings: loaded.workload,
                updated_at: new Date().toISOString(),
                // `?? null` statt `undefined`: JSON.stringify laesst ein `undefined`-
                // Feld weg, dann bliebe in der Zeile die ID des vorigen Autors neben
                // dem neuen `updated_by_name` stehen (Befund I3).
                updated_by: currentUser?.id ?? null,
                updated_by_name: name,
            }, { onConflict: 'company_name, year' });
            if (error) throw error;
            const date = new Date().toISOString();
            setConfig((p) => (p && p.year === loaded.year ? { ...p, lastEdit: { name, date } } : p));
            return true;
        } catch (e) {
            console.error(e);
            return false;
        } finally {
            setSaving(false);
        }
    }, [companyName, currentUser, loaded]);

    return {
        offsets: loaded?.offsets ?? NO_OFFSETS,
        workload: loaded?.workload ?? DEFAULT_WORKLOAD,
        lastEdit: loaded?.lastEdit ?? null,
        failed: loaded?.failed ?? false,
        saveBlocked: loaded
            ? (loaded.failed ? 'Einstellungen konnten nicht geladen werden — Speichern gesperrt.' : null)
            : 'Konfiguration wird geladen…',
        saving,
        setOffsets,
        setWorkload,
        save,
    };
};
